import type { FunctionComponent } from "react"

import dynamic from "next/dynamic"
import { useEffect, useState } from "react"

import { setCookie } from "@/modules/client/cookies/setCookie"
import { NOTIFICATION_BANNER_COOKIE_NAME } from "@/modules/notificationBanner/constants"
import { useNotificationBanner } from "@/modules/notificationBanner/hooks/useNotificationBanner"
import { getHiddenBannerCookieValue } from "@/modules/notificationBanner/utils/getHiddenBannerCookieValue"

export const NOTIFICATION_BANNER_CLOSED_EVENT = "notificationBannerClosed"

const DynamicNotificationBanner = dynamic(() =>
  import("@/modules/notificationBanner/Components/NotificationBanner").then(mod => mod.NotificationBanner)
)

export const NotificationBannerContainer: FunctionComponent = () => {
  const { data: notificationBanner } = useNotificationBanner()
  const [isBannerVisible, setIsBannerVisible] = useState(!!notificationBanner)

  // If the call is made client-side, the value will change after the first render.
  useEffect(() => {
    setIsBannerVisible(!!notificationBanner)
  }, [notificationBanner])

  useEffect(() => {
    if (!isBannerVisible) {
      window.dispatchEvent(new CustomEvent(NOTIFICATION_BANNER_CLOSED_EVENT))
    }
  }, [isBannerVisible])

  if (!notificationBanner || !isBannerVisible) return null

  const onClose = (): void => {
    const hiddenBannerCookieValue = getHiddenBannerCookieValue(notificationBanner)
    // Add a cookie to remember that the user has dismissed the banner.
    setCookie({ name: NOTIFICATION_BANNER_COOKIE_NAME, value: hiddenBannerCookieValue })
    setIsBannerVisible(false)
  }

  return <DynamicNotificationBanner onClose={onClose} text={notificationBanner.text} />
}
